import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import upperFirst from "lodash/upperFirst";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";

import { isAutocompleteInputClear } from "../../../utils/autocompleteClearReason";

export default function AutoExpandEntitySelect({
  label,
  name = "",
  value,
  onChange,
  suggestions = [],
  disabled = false,
  getOptionLabel = option => option.name || "",
  renderOption,
  multiline = false,
  minRows = 1,
  maxRows = 3,
}) {
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);
  const selectedEntity = suggestions.find(e => e.id === value) || null;
  const selectedLabel = selectedEntity ? getOptionLabel(selectedEntity) : "";
  const [inputValue, setInputValue] = useState(selectedLabel);

  useEffect(() => {
    setInputValue(selectedLabel);
  }, [selectedLabel, value]);

  const focusInput = useCallback(() => {
    const inputEl =
      rootRef.current?.querySelector("input") || rootRef.current?.querySelector("textarea");
    inputEl?.focus();
  }, []);

  const openAndFocus = useCallback(() => {
    const run = () => {
      setOpen(true);
      focusInput();
    };

    if (typeof requestAnimationFrame !== "undefined") {
      requestAnimationFrame(run);
      return;
    }

    setTimeout(run, 0);
  }, [focusInput]);

  const filterOptions = useMemo(() => {
    return (options, { inputValue }) => {
      if (!inputValue) return options;

      const searchTerm = inputValue.toLowerCase();
      return options.filter(option => {
        const optionLabel = String(getOptionLabel(option) || "").toLowerCase();
        return optionLabel.includes(searchTerm);
      });
    };
  }, [getOptionLabel]);

  return (
    <Box
      ref={rootRef}
      sx={{
        width: "100%",
        ...(multiline
          ? {
              height: "100%",
              display: "flex",
              "& .MuiAutocomplete-root": {
                width: "100%",
                height: "100%",
              },
              "& .MuiFormControl-root": {
                height: "100%",
              },
            }
          : {}),
        "& .MuiAutocomplete-option": {
          whiteSpace: "normal",
          overflowWrap: "anywhere",
          lineHeight: 1.3,
        },
        "& .MuiOutlinedInput-root": {
          ...(multiline ? { height: "100%", alignItems: "flex-start" } : {}),
        },
        "& textarea.MuiOutlinedInput-input": {
          overflowWrap: "anywhere",
          resize: "none",
        },
      }}
    >
      <Autocomplete
        options={suggestions}
        getOptionLabel={getOptionLabel}
        value={selectedEntity}
        inputValue={inputValue}
        selectOnFocus
        open={open}
        onOpen={() => setOpen(true)}
        onClose={(event, reason) => {
          if (reason === "clear") return;
          setOpen(false);
        }}
        onInputChange={(event, newInputValue, reason) => {
          if (reason === "reset") return;

          if (reason === "blur") {
            setInputValue(selectedLabel);
            return;
          }

          setInputValue(newInputValue);
        }}
        onChange={(event, newValue, reason) => {
          setInputValue(newValue ? getOptionLabel(newValue) : "");
          if (!isAutocompleteInputClear(reason, event)) {
            onChange(newValue, reason, event);
            if (reason === "clear") openAndFocus();
          }
        }}
        disabled={disabled}
        isOptionEqualToValue={(option, optionValue) => option.id === optionValue?.id}
        getOptionKey={option => option?.id ?? getOptionLabel(option)}
        filterOptions={filterOptions}
        renderOption={renderOption}
        renderInput={params => (
          <TextField
            {...params}
            label={label}
            name={name}
            variant="outlined"
            size="small"
            data-id={`select${upperFirst(name)}`}
            fullWidth
            multiline={multiline}
            minRows={multiline ? minRows : undefined}
            maxRows={multiline ? maxRows : undefined}
            inputProps={{
              ...params.inputProps,
              onKeyDown: event => {
                if (params.inputProps.onKeyDown) params.inputProps.onKeyDown(event);
                if (multiline && event.key === "Enter") event.preventDefault();
              },
            }}
          />
        )}
      />
    </Box>
  );
}
